// TopBar — Fusion 360-style ribbon с workspace-вкладками сверху.
//
// Структура:
//   ┌─────────────────────────────────────────────────────────┐
//   │ [SOLID] SURFACE  SHEET METAL  TOOLS                     │  ← workspace tabs
//   ├─────────────────────────────────────────────────────────┤
//   │ ┃ Sketch │ Box  Extrude · Revolve · Box · Cyl · ...     │  ← ribbon
//   │ ┃        │      ┃ Fillet · Chamfer · ┃ Combine          │
//   │ ┃        │ CREATE ▾│ MODIFY ▾│ ASSEMBLE ▾│ ...           │  ← group labels
//   └─────────────────────────────────────────────────────────┘
//
// Внутри группы: 1 «main» большая иконка + остальные обычным размером;
// под иконками лейбл. Клик по main = direct action; клик по другим = direct;
// клик по ▾ снизу группы = dropdown с расширенным списком.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import CommandSearch from './CommandSearch';
import RU from '../i18n/ru';
import useCadStore from '../store/useCadStore';
import { findOpenChains } from '../sketch/sketchProfiles';
import { getGeometryClient } from '../geometryClient';
import {
    downloadDocumentFile, openDocumentFile, loadDocumentFromJson,
} from '../documentIO';
import * as autosave from '../autosaveDb';
import { buildFaceProfile } from '../sketch/faceProfileBuilder';
import {
    IconSketch, IconExtrude, IconRevolve, IconSweep, IconLoft,
    IconBox, IconCylinder, IconSphere, IconTorus, IconCoil, IconPipe, IconThread,
    IconRib, IconWeb, IconHole, IconPattern, IconCircPattern, IconMirror,
    IconFillet, IconChamfer, IconShell, IconDraft, IconScale,
    IconCombine, IconMove, IconDelete,
    IconJoint, IconComponent,
    IconPlane, IconAxis, IconPoint,
    IconInspect, IconMeasure,
    IconInsert, IconSelect, IconFile,
    IconChevronDown,
    IconForm, IconDerive, IconThicken, IconBoundaryFill,
    IconMesh, IconMeshSectionSketch, IconBaseFeature, IconPcb,
    IconPressPull, IconOffsetFace, IconReplaceFace, IconSplitFace,
    IconSilhouetteSplit, IconAlign, IconMaterial, IconAppearance,
    IconManageMaterials, IconParameters, IconComputeAll,
    // Иконки режима эскиза
    IconSketchLine, IconSketchRect, IconSketchCircle, IconSketchArc,
    IconSketchPolygon, IconSketchSpline, IconSketchPoint, IconSketchText,
    IconSketchEllipse, IconSketchSlot,
    IconSketchFillet, IconSketchTrim, IconSketchExtend, IconSketchOffset,
    IconSketchMirror, IconSketchPattern, IconSketchProject, IconSketchDimension,
    IconConstructionLine,
    IconConstraintH, IconConstraintV, IconConstraintParallel,
    IconConstraintPerpendicular, IconConstraintCoincident, IconConstraintTangent,
    IconConstraintEqual, IconConstraintMidpoint, IconConstraintSymmetric,
    IconConstraintFix, IconConstraintConcentric,
    IconFinishSketch,
} from './icons';
import cl from './TopBar.module.css';
import { toast } from './Toasts';


// Workspace-вкладки. Только TELO реально работает; остальные — заглушки.
const WORKSPACES = [
    { id: 'solid',      label: 'ТЕЛО',         active: true  },
    { id: 'surface',    label: 'ПОВЕРХНОСТЬ',  active: false },
    { id: 'sheetmetal', label: 'ЛИСТ',         active: false },
    { id: 'tools',      label: 'ИНСТРУМЕНТЫ',  active: false },
];


// Структура ribbon: список групп 1:1 с Fusion 360 SOLID workspace.
// Каждая группа имеет:
//   primary — большие иконки в горизонтальном ряду (видны сразу);
//   more    — полный список (выпадашка с ▾ снизу группы).
// secondary НЕ используем — это сетка 2×N, в Fusion такого нет в верхнем ряду.
const RIBBON = [
    {
        id: 'sketch',
        label: 'ЭСКИЗ',
        primary: [
            { id: 'newSketch', label: RU.create.newSketch, icon: IconSketch, color: '#6dc36d' },
        ],
    },
    {
        // СОЗДАТЬ: в верхней панели Fusion 6 главных иконок —
        // Extrude · Revolve · Sweep · Loft · Form · Box.
        // Цвета взяты из Fusion 360: голубой/жёлтый/оранжевый/зелёный/фиолетовый.
        id: 'create',
        label: 'СОЗДАТЬ',
        primary: [
            { id: 'extrude',     label: RU.create.extrude,     icon: IconExtrude,     color: '#5fb8e7' },
            { id: 'hole',        label: RU.create.hole,        icon: IconHole,        color: '#5fb8e7' },
            { id: 'mirror',      label: RU.create.mirror,      icon: IconMirror,      color: '#a25fb8' },
            { id: 'pattern',     label: RU.create.rectPattern, icon: IconPattern,     color: '#e8b73a' },
            { id: 'circPattern', label: RU.create.circPattern, icon: IconCircPattern, color: '#e8b73a' },
            { id: 'box',         label: RU.create.box,         icon: IconBox,         color: '#e88a3a' },
        ],
        // Полный список «more» — порядок и группировка 1:1 с Fusion.
        more: [
            { id: 'newComponent', label: RU.create.newComponent, icon: IconComponent, disabled: true },
            { id: 'newSketch2',   label: RU.create.newSketch,    icon: IconSketch },
            { id: 'createForm2',  label: RU.create.createForm,   icon: IconForm,      disabled: true },
            { id: 'derive',       label: RU.create.derive,       icon: IconDerive,    disabled: true },
            { id: '__sep1', sep: true },
            { id: 'extrude2', label: RU.create.extrude, icon: IconExtrude },
            { id: 'revolve2', label: RU.create.revolve, icon: IconRevolve },
            { id: 'sweep2',   label: RU.create.sweep,   icon: IconSweep },
            { id: 'loft2',    label: RU.create.loft,    icon: IconLoft },
            { id: 'rib',      label: RU.create.rib,     icon: IconRib },
            { id: 'web',      label: RU.create.web,     icon: IconWeb },
            { id: 'hole',     label: RU.create.hole,    icon: IconHole },
            { id: 'thread',   label: RU.create.thread,  icon: IconThread,  disabled: true },
            { id: '__sep2', sep: true },
            { id: 'box2',      label: RU.create.box,      icon: IconBox },
            { id: 'cylinder',  label: RU.create.cylinder, icon: IconCylinder },
            { id: 'sphere',    label: RU.create.sphere,   icon: IconSphere },
            { id: 'torus',     label: RU.create.torus,    icon: IconTorus },
            { id: 'coil',      label: RU.create.coil,     icon: IconCoil },
            { id: 'pipe',      label: RU.create.pipe,     icon: IconPipe,    disabled: true },
            { id: '__sep3', sep: true },
            { id: 'pattern',      label: RU.create.rectPattern,  icon: IconPattern },
            { id: 'circPattern',  label: RU.create.circPattern,  icon: IconCircPattern },
            { id: 'mirror',       label: RU.create.mirror,       icon: IconMirror },
            { id: 'thicken',      label: RU.create.thicken,      icon: IconThicken,      disabled: true },
            { id: 'boundaryFill', label: RU.create.boundaryFill, icon: IconBoundaryFill, disabled: true },
            { id: '__sep4', sep: true },
            { id: 'createMesh',        label: RU.create.createMesh,        icon: IconMesh,              disabled: true },
            { id: 'meshSectionSketch', label: RU.create.meshSectionSketch, icon: IconMeshSectionSketch, disabled: true },
            { id: 'baseFeature',       label: RU.create.baseFeature,       icon: IconBaseFeature,       disabled: true },
            { id: 'pcb3d',             label: RU.create.pcb3d,             icon: IconPcb,               disabled: true },
        ],
    },
    {
        // ИЗМЕНИТЬ: в верхней панели Fusion 4 главных иконки —
        // Press Pull · Fillet · Chamfer · Shell + Move/Copy.
        // Цвета: pressPull=голубой, fillet/chamfer=зелёный, shell=голубой.
        id: 'modify',
        label: 'ИЗМЕНИТЬ',
        primary: [
            { id: 'pressPull', label: RU.modify.pressPull, icon: IconPressPull, color: '#5fb8e7', disabled: true },
            { id: 'fillet',    label: RU.modify.fillet,    icon: IconFillet,    color: '#6dc36d' },
            { id: 'chamfer',   label: RU.modify.chamfer,   icon: IconChamfer,   color: '#6dc36d' },
            { id: 'shell',     label: RU.modify.shell,     icon: IconShell,     color: '#5fb8e7' },
            { id: 'moveCopy',  label: RU.modify.moveCopy,  icon: IconMove,      color: '#9aa6b3' },
        ],
        // Полный список «more» — все 20 пунктов Fusion в порядке скриншота.
        more: [
            { id: 'pressPull2', label: RU.modify.pressPull, icon: IconPressPull, disabled: true },
            { id: 'fillet2',    label: RU.modify.fillet,    icon: IconFillet },
            { id: 'chamfer2',   label: RU.modify.chamfer,   icon: IconChamfer },
            { id: 'shell2',     label: RU.modify.shell,     icon: IconShell },
            { id: 'draft',      label: RU.modify.draft,     icon: IconDraft,    disabled: true },
            { id: 'scale',      label: RU.modify.scale,     icon: IconScale },
            { id: 'combine',    label: RU.modify.combine,   icon: IconCombine },
            { id: '__sep1', sep: true },
            { id: 'offsetFace',  label: RU.modify.offsetFace,  icon: IconOffsetFace,      disabled: true },
            { id: 'replaceFace', label: RU.modify.replaceFace, icon: IconReplaceFace,     disabled: true },
            { id: 'splitFace',   label: RU.modify.splitFace,   icon: IconSplitFace,       disabled: true },
            { id: 'splitBody',   label: RU.modify.splitBody,   icon: IconCombine,         disabled: true },
            { id: 'silhouetteSplit', label: RU.modify.silhouetteSplit, icon: IconSilhouetteSplit, disabled: true },
            { id: '__sep2', sep: true },
            { id: 'moveCopy2', label: RU.modify.moveCopy, icon: IconMove },
            { id: 'align',     label: RU.modify.align,    icon: IconAlign,  disabled: true },
            { id: 'delete',    label: RU.modify.delete,   icon: IconDelete, disabled: true },
            { id: '__sep3', sep: true },
            { id: 'physicalMaterial', label: RU.modify.physicalMaterial, icon: IconMaterial,        disabled: true },
            { id: 'appearance',       label: RU.modify.appearance,       icon: IconAppearance,      disabled: true },
            { id: 'manageMaterials',  label: RU.modify.manageMaterials,  icon: IconManageMaterials, disabled: true },
            { id: '__sep4', sep: true },
            { id: 'changeParameters', label: RU.modify.changeParameters, icon: IconParameters },
            { id: 'computeAll',       label: RU.modify.computeAll,       icon: IconComputeAll,  disabled: true },
        ],
    },
    {
        // СБОРКА: Fusion показывает 2 иконки (New Component + Joint).
        // Цвет — жёлтый (как в Fusion для сборочных операций).
        id: 'assemble',
        label: 'СБОРКА',
        primary: [
            { id: 'newComponent', label: RU.create.newComponent, icon: IconComponent, color: '#e8b73a', disabled: true },
            { id: 'joint',        label: 'Соединение',           icon: IconJoint,     color: '#e8b73a' },
        ],
    },
    {
        // ПОСТРОИТЬ: главная иконка — Offset Plane (оранжевая в Fusion).
        id: 'construct',
        label: 'ПОСТРОИТЬ',
        primary: [
            { id: 'offsetPlane', label: RU.construct?.offsetPlane || 'Плоскость со смещением', icon: IconPlane, color: '#e88a3a' },
            { id: 'axisThroughTwoPoints', label: 'Рабочая ось', icon: IconAxis, color: '#e88a3a' },
        ],
        more: [
            { id: 'offsetPlane2',     label: 'Плоскость со смещением',                   icon: IconPlane },
            { id: 'planeAtAngle',     label: RU.construct.planeAtAngle,                  icon: IconPlane },
            { id: 'midplane',         label: RU.construct.midplane,                      icon: IconPlane },
            { id: 'planeThroughThree', label: RU.construct.planeThroughThree,            icon: IconPlane },
            { id: '__sep1', sep: true },
            { id: 'axisThroughTwoPoints', label: 'Ось через 2 точки',                    icon: IconAxis },
            { id: 'axisFromCylinder',     label: RU.construct.axisFromCylinder,          icon: IconAxis,  disabled: true },
            { id: '__sep2', sep: true },
            { id: 'pointAtCenter',     label: 'Точка',                                   icon: IconPoint, disabled: true },
        ],
    },
    {
        // АНАЛИЗ: главная иконка — Measure (линейка). Светло-серый/жёлтый.
        id: 'inspect',
        label: 'АНАЛИЗ',
        primary: [
            { id: 'measure', label: 'Измерение', icon: IconMeasure, color: '#cdd6e0' },
            { id: 'massProps', label: 'Масса/объём', icon: IconInspect, color: '#cdd6e0' },
        ],
        more: [
            { id: 'measure2', label: 'Измерение', icon: IconMeasure },
            { id: 'massProps', label: 'Масса/объём', icon: IconInspect },
        ],
    },
    {
        // ВСТАВИТЬ: главная иконка — STEP-импорт. Светло-серый.
        id: 'insert',
        label: 'ВСТАВИТЬ',
        primary: [
            { id: 'importSTEP', label: RU.file.importSTEP, icon: IconInsert, color: '#cdd6e0' },
        ],
        more: [
            { id: 'importSTEP2', label: RU.file.importSTEP, icon: IconInsert },
            { id: 'importSTL',   label: RU.file.importSTL,  icon: IconInsert, disabled: true },
        ],
    },
    {
        // ВЫБРАТЬ: курсор-стрелка.
        id: 'select',
        label: 'ВЫБРАТЬ',
        primary: [
            { id: 'selectMode', label: RU.toolbar.select, icon: IconSelect, color: '#cdd6e0', disabled: true },
        ],
    },
];


// =====================================================================
// === SKETCH RIBBON — ribbon при mode === 'sketch' ===================
// =====================================================================
// При входе в режим эскиза TopBar отрисовывает этот ribbon вместо обычного.
// Структура аналогична Fusion 360 SKETCH workspace:
//   СОЗДАТЬ (line/rect/circle/...) | ИЗМЕНИТЬ (fillet/trim/...) |
//   ОГРАНИЧЕНИЯ (horizontal/vertical/...) | АНАЛИЗ | ВСТАВИТЬ | ВЫБРАТЬ |
//   ЗАВЕРШИТЬ ЭСКИЗ (зелёная кнопка справа).
//
// Реализованные инструменты: line, rectangle, circle (через
// setActiveSketchTool). Остальные — заглушки `disabled`.
const SKETCH_RIBBON = [
    {
        id: 'sketchCreate',
        label: 'СОЗДАТЬ',
        primary: [
            { id: 'line',      label: RU.sketch.line,      icon: IconSketchLine,    color: '#5fb8e7' },
            { id: 'rectangle', label: RU.sketch.rectangle, icon: IconSketchRect,    color: '#5fb8e7' },
            { id: 'circle',    label: RU.sketch.circle,    icon: IconSketchCircle,  color: '#5fb8e7' },
            { id: 'arc',       label: RU.sketch.arc,       icon: IconSketchArc,     color: '#5fb8e7' },
            { id: 'polygon',   label: RU.sketch.polygon,   icon: IconSketchPolygon, color: '#5fb8e7' },
            { id: 'sketchDimension', label: RU.sketch.sketchDimension, icon: IconSketchDimension, color: '#e8b73a' },
            { id: 'spline',    label: RU.sketch.spline,    icon: IconSketchSpline,  color: '#5fb8e7' },
        ],
        more: [
            { id: 'line2',      label: RU.sketch.line,      icon: IconSketchLine },
            { id: 'rectangle2', label: RU.sketch.rectangle, icon: IconSketchRect },
            { id: 'circle2',    label: RU.sketch.circle,    icon: IconSketchCircle },
            { id: 'arc2',       label: RU.sketch.arc,       icon: IconSketchArc },
            { id: 'polygon2',   label: RU.sketch.polygon,   icon: IconSketchPolygon },
            { id: 'ellipse',    label: RU.sketch.ellipse,   icon: IconSketchEllipse },
            { id: 'slot',       label: RU.sketch.slot,      icon: IconSketchSlot },
            { id: 'spline2',    label: RU.sketch.spline,    icon: IconSketchSpline },
            { id: '__sep1', sep: true },
            { id: 'point',          label: RU.sketch.point,            icon: IconSketchPoint },
            { id: 'text',           label: RU.sketch.text,             icon: IconSketchText },
            { id: 'constructionLine', label: RU.sketch.constructionLine, icon: IconConstructionLine },
            { id: '__sep2', sep: true },
            { id: 'sketchDimension', label: RU.sketch.sketchDimension, icon: IconSketchDimension },
            { id: 'project',         label: RU.sketch.project,         icon: IconSketchProject },
        ],
    },
    {
        id: 'sketchModify',
        label: 'ИЗМЕНИТЬ',
        primary: [
            { id: 'sketchFillet', label: RU.sketch.filletSketch, icon: IconSketchFillet, color: '#6dc36d' },
            { id: 'trim',         label: RU.sketch.trim,         icon: IconSketchTrim,   color: '#6dc36d' },
            { id: 'extend',       label: RU.sketch.extend,       icon: IconSketchExtend, color: '#6dc36d' },
            { id: 'sketchOffset', label: RU.sketch.offset,       icon: IconSketchOffset, color: '#6dc36d' },
        ],
        more: [
            { id: 'sketchFillet2', label: RU.sketch.filletSketch, icon: IconSketchFillet },
            { id: 'trim2',         label: RU.sketch.trim,         icon: IconSketchTrim },
            { id: 'extend2',       label: RU.sketch.extend,       icon: IconSketchExtend },
            { id: 'sketchOffset2', label: RU.sketch.offset,       icon: IconSketchOffset },
            { id: '__sep1', sep: true },
            { id: 'sketchMirror',  label: RU.sketch.mirror,       icon: IconSketchMirror },
            { id: 'sketchPattern', label: RU.sketch.pattern,      icon: IconSketchPattern },
        ],
    },
    {
        id: 'constraints',
        label: 'ОГРАНИЧЕНИЯ',
        // primary — реализованные ограничения (на виду).
        // more — заглушки (горизонталь/вертикаль/… ещё disabled).
        primary: [
            { id: 'tangent',    label: RU.constraints.tangent,    icon: IconConstraintTangent,    color: '#e8554e' },
            { id: 'concentric', label: RU.constraints.concentric, icon: IconConstraintConcentric, color: '#e8554e' },
            { id: 'midpoint',   label: RU.constraints.midpoint,   icon: IconConstraintMidpoint,   color: '#e8554e' },
            { id: 'symmetric',  label: RU.constraints.symmetric,  icon: IconConstraintSymmetric,  color: '#e8554e' },
            { id: 'fix',        label: RU.constraints.fix,        icon: IconConstraintFix,        color: '#e8554e' },
        ],
        more: [
            { id: 'tangent2',    label: RU.constraints.tangent,    icon: IconConstraintTangent },
            { id: 'concentric2', label: RU.constraints.concentric, icon: IconConstraintConcentric },
            { id: 'midpoint2',   label: RU.constraints.midpoint,   icon: IconConstraintMidpoint },
            { id: 'symmetric2',  label: RU.constraints.symmetric,  icon: IconConstraintSymmetric },
            { id: 'fix2',        label: RU.constraints.fix,        icon: IconConstraintFix },
            { id: '__sep1', sep: true },
            { id: 'horizontal',    label: RU.constraints.horizontal,    icon: IconConstraintH },
            { id: 'vertical',      label: RU.constraints.vertical,      icon: IconConstraintV },
            { id: 'parallel',      label: RU.constraints.parallel,      icon: IconConstraintParallel },
            { id: 'perpendicular', label: RU.constraints.perpendicular, icon: IconConstraintPerpendicular },
            { id: 'coincident',    label: RU.constraints.coincident,    icon: IconConstraintCoincident },
            { id: 'equal',         label: RU.constraints.equal,         icon: IconConstraintEqual },
        ],
    },
    {
        id: 'sketchInspect',
        label: 'АНАЛИЗ',
        primary: [
            { id: 'sketchMeasure', label: 'Измерение', icon: IconMeasure, color: '#cdd6e0', disabled: true },
        ],
    },
    {
        id: 'sketchInsert',
        label: 'ВСТАВИТЬ',
        primary: [
            { id: 'insertSvg', label: 'Вставить SVG', icon: IconInsert, color: '#cdd6e0', disabled: true },
        ],
    },
    {
        id: 'sketchSelect',
        label: 'ВЫБРАТЬ',
        primary: [
            { id: 'selectMode', label: RU.toolbar.select, icon: IconSelect, color: '#cdd6e0' },
        ],
    },
    {
        // ЗАВЕРШИТЬ ЭСКИЗ — компактная зелёная кнопка-кружок справа.
        // compact=true — без подписи под иконкой и без лейбла группы снизу.
        id: 'finishSketch',
        compact: true,
        primary: [
            { id: 'finishSketch', label: RU.sketch.finish, icon: IconFinishSketch, color: '#3aa84a' },
        ],
    },
];


// Файловое меню (отдельная кнопка слева — выпадашка).
const FILE_MENU = [
    { id: 'newDoc',     label: RU.file.new },
    { id: 'open',       label: RU.file.open },
    { id: '__sep1', sep: true },
    { id: 'saveAs',     label: RU.file.saveAs },
    { id: '__sep2', sep: true },
    { id: 'cloudOpen',  label: '☁ Открыть из облака' },
    { id: 'cloudSave',  label: '☁ Сохранить в облако' },
    { id: '__sep3', sep: true },
    { id: 'exportSTL',  label: RU.file.exportSTL },
    { id: 'exportSTEP', label: RU.file.exportSTEP },
    { id: 'exportOBJ',  label: RU.file.exportOBJ },
];


// === RibbonGroup ===
// Одна группа ribbon'а: primary-большие иконки слева, secondary-мелкие после,
// под ними — лейбл группы с ▾ который открывает more-dropdown.
// activeId — для подсветки toggle-инструмента (например, активная sketch-tool).
// compact — компактный режим (только иконка-кружок, без лейбла группы и
// подписи под кнопкой). Используется для финишной кнопки эскиза.
const RibbonGroup = ({ group, isMoreOpen, onToggleMore, onItemClick, activeId }) => {
    const { primary = [], secondary = [], more = [], label, compact } = group;
    const hasMore = more && more.length > 0;

    // Компактный режим — рендерим только круглую кнопку без лейблов
    if (compact) {
        return (
            <div className={cl.compactGroup} data-group-id={group.id}>
                {primary.map((it) => {
                    const Icon = it.icon;
                    return (
                        <button
                            key={it.id}
                            className={cl.compactBtn}
                            onClick={() => onItemClick(it)}
                            title={it.label}
                        >
                            {Icon && <Icon size={20} color="#fff"/>}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={cl.group} data-group-id={group.id}>
            <div className={cl.groupBody}>
                {/* Primary — большие иконки */}
                {primary.map((it) => (
                    <ToolButton
                        key={it.id}
                        item={it}
                        size="lg"
                        active={activeId === it.id}
                        onClick={() => onItemClick(it)}
                    />
                ))}

                {/* Secondary — обычные иконки в сетке 2-в-ряд по высоте */}
                {secondary.length > 0 && (
                    <div className={cl.secondaryGrid}>
                        {secondary.map((it) => (
                            <ToolButton
                                key={it.id}
                                item={it}
                                size="sm"
                                active={activeId === it.id}
                                onClick={() => onItemClick(it)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Метка группы внизу + ▾ если есть more */}
            <button
                className={`${cl.groupLabel} ${hasMore ? cl.groupLabelClickable : ''}`}
                onClick={hasMore ? onToggleMore : undefined}
                title={hasMore ? 'Больше команд' : undefined}
            >
                <span>{label}</span>
                {hasMore && <span className={cl.groupArrow}><IconChevronDown size={9}/></span>}
            </button>

            {/* Dropdown «more» */}
            {hasMore && isMoreOpen && (
                <div className={cl.moreDropdown}>
                    {more.map((it) => {
                        if (it.sep) return <div key={it.id} className={cl.menuSep}/>;
                        const Icon = it.icon;
                        return (
                            <button
                                key={it.id}
                                className={`${cl.menuItem} ${it.disabled ? cl.menuItemDisabled : ''}`}
                                onClick={() => onItemClick(it)}
                                disabled={it.disabled}
                            >
                                {Icon && (
                                    <span className={cl.menuItemIcon}>
                                        <Icon size={18}/>
                                    </span>
                                )}
                                <span className={cl.menuItemLabel}>{it.label}</span>
                                {it.disabled && <span className={cl.soonBadge}>скоро</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


// === ToolButton ===
// Кнопка с SVG-иконкой и подписью под ней (как в Fusion).
// active — кнопка-toggle включена (например, активный sketch-tool).
// item.color — цвет иконки. На disabled цвет НЕ применяется.
const ToolButton = ({ item, size, onClick, active }) => {
    const Icon = item.icon;
    const big = size === 'lg';
    const cls = `${cl.toolBtn} ${big ? cl.toolBtnLg : cl.toolBtnSm} ${item.disabled ? cl.toolBtnDisabled : ''} ${active ? cl.toolBtnActive : ''}`;
    const iconColor = item.disabled ? undefined : item.color;
    return (
        <button
            className={cls}
            onClick={() => !item.disabled && onClick()}
            disabled={item.disabled}
            title={item.label + (item.disabled ? ' — скоро' : '')}
        >
            <span className={cl.toolBtnIcon}>
                {Icon ? <Icon size={big ? 30 : 22} color={iconColor}/> : null}
            </span>
            {big && <span className={cl.toolBtnLabel}>{item.label}</span>}
        </button>
    );
};


const TopBar = ({ onCreateTestCube }) => {
    const [openMenu, setOpenMenu] = useState(null);  // 'file' | <ribbon-group-id-more>
    const [activeWorkspace, setActiveWorkspace] = useState('solid');
    const containerRef = useRef(null);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    // Текущий режим: 'view3d' | 'select_plane' | 'sketch' | 'feature_edit'.
    // В режиме 'sketch' рендерим SKETCH_RIBBON вместо обычного.
    const mode = useCadStore((s) => s.mode);
    const activeSketchTool = useCadStore((s) => s.activeSketchTool);
    const isSketchMode = mode === 'sketch';
    const ribbon = isSketchMode ? SKETCH_RIBBON : RIBBON;

    /*
     * Поиск команд по клавише S — Toolbox из Fusion.
     *
     * Команд много, они разложены по вкладкам и спискам «Больше команд», и
     * помнить, где лежит нужная, невозможно. Нажал S, набрал пару букв —
     * быстрее, чем искать иконку глазами.
     *
     * Ищем по ОБЕИМ лентам сразу (тело и эскиз): человек помнит название
     * команды, а не режим, в котором она доступна.
     */
    const [searchOpen, setSearchOpen] = useState(false);
    const allCommands = useMemo(() => {
        const out = [];
        for (const [rib, where] of [[RIBBON, ''], [SKETCH_RIBBON, 'эскиз']]) {
            for (const g of rib) {
                for (const it of (g.primary || [])) {
                    if (!it || it.sep) continue;
                    // Одноимённые команды есть и в ленте тела, и в ленте
                    // эскиза («Скругление»). Без пометки режима их в списке
                    // не различить — человек не поймёт, какую выбирает.
                    // У компактных групп (кнопка «Завершить эскиз») своего
                    // заголовка нет — берём подпись самой команды, иначе в
                    // списке выводилось «undefined».
                    const gl = g.label || (where ? '' : 'КОМАНДЫ');
                    out.push({
                        ...it,
                        groupId: g.id,
                        groupLabel: [gl, where].filter(Boolean).join(' · '),
                    });
                }
            }
        }
        return out;
    }, []);

    /*
     * Горячие клавиши — раскладка Fusion 360.
     *
     * Клавиша зависит от РЕЖИМА: в эскизе L это «Линия», в режиме тела
     * та же логика неприменима. Так же устроено в Fusion, и мышечная
     * память переносится один в один.
     *
     * Русские буквы указаны для каждой латинской: переключать раскладку
     * ради горячей клавиши — то, что раздражает больше всего.
     */
    const HOTKEYS_BODY = {
        e: ['create', 'extrude'],        у: ['create', 'extrude'],
        q: ['modify', 'pressPull'],      й: ['modify', 'pressPull'],
        f: ['modify', 'fillet'],         а: ['modify', 'fillet'],
        h: ['create', 'hole'],           р: ['create', 'hole'],
        m: ['modify', 'moveCopy'],       ь: ['modify', 'moveCopy'],
        j: ['assemble', 'joint'],        о: ['assemble', 'joint'],
    };
    const HOTKEYS_SKETCH = {
        l: ['create', 'line'],           д: ['create', 'line'],
        r: ['create', 'rectangle'],      к: ['create', 'rectangle'],
        c: ['create', 'circle'],         с: ['create', 'circle'],
        a: ['create', 'arc'],            ф: ['create', 'arc'],
        d: ['modify', 'sketchDimension'], в: ['modify', 'sketchDimension'],
        t: ['modify', 'trim'],           е: ['modify', 'trim'],
        o: ['modify', 'sketchOffset'],   щ: ['modify', 'sketchOffset'],
        p: ['modify', 'project'],        з: ['modify', 'project'],
    };

    // Клавиша S открывает поиск — но не когда набирают текст в поле.
    useEffect(() => {
        const onKey = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target;
            const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
                || t.isContentEditable);
            if (typing) return;
            const k = String(e.key || '').toLowerCase();
            if (k === 's' || k === 'ы') {
                e.preventDefault();
                setSearchOpen((v) => !v);
                return;
            }
            // Модальные окна и радиальное меню обрабатывают клавиши сами.
            if (searchOpen) return;
            const table = isSketchMode ? HOTKEYS_SKETCH : HOTKEYS_BODY;
            const hit = table[k];
            if (!hit) return;
            // Команду ищем в ленте: если её там нет или она отключена —
            // клавиша не должна «проглатываться» молча.
            const cmd = allCommands.find(
                (c) => c.id === hit[1] && !c.disabled,
            );
            if (!cmd) return;
            e.preventDefault();
            dispatchAction(cmd.groupId, cmd.id);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    // Закрытие меню по клику вне TopBar
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpenMenu(null);
            }
        };
        window.addEventListener('mousedown', handler);
        return () => window.removeEventListener('mousedown', handler);
    }, []);

    // Универсальный диспетчер кликов по любой команде ribbon/file-меню.
    const dispatchAction = (groupId, itemIdRaw) => {
        // Дубли в more-меню имеют суффикс '2' (extrude2 и т.п.) чтобы не было
        // коллизий React key — нормализуем перед диспатчем.
        const itemId = itemIdRaw.endsWith('2') ? itemIdRaw.slice(0, -1) : itemIdRaw;
        // === FILE menu ===
        if (groupId === 'file') {
            if (itemId === 'newDoc')    { handleNewDocument();    return; }
            if (itemId === 'open')      { handleOpenDocument();   return; }
            if (itemId === 'saveAs')    { handleSaveAsDocument(); return; }
            if (itemId === 'cloudOpen') { useCadStore.getState().setCloudModalOpen(true); return; }
            if (itemId === 'cloudSave') { handleCloudSave();      return; }
            if (itemId === 'exportSTL')  { handleExportSTL(bodyMeshes);              return; }
            if (itemId === 'exportSTEP') { handleExportFormat(bodyMeshes, 'step');   return; }
            if (itemId === 'exportOBJ')  { handleExportFormat(bodyMeshes, 'obj');    return; }
        }
        // === SKETCH ===
        if (groupId === 'sketch' && itemId === 'newSketch') { handleNewSketch(); return; }
        // === CREATE ===
        if (groupId === 'create') {
            if (itemId === 'newSketch') { handleNewSketch(); return; }
            if (itemId === 'extrude')   { handleNewExtrude(); return; }
            if (itemId === 'revolve2')  { handleNewRevolve(); return; }
            if (itemId === 'sweep2')    { useCadStore.getState().setPendingFeature({ kind: 'sweep', profileRef: null, pathRef: null, operation: 'new' }); return; }
            if (itemId === 'loft2')     { useCadStore.getState().setPendingFeature({ kind: 'loft', profileRefs: [], operation: 'new' }); return; }
            // Текст: если выделена грань — буквы лягут на неё (как в 123D:
            // кликнул грань → «T»). Иначе спросим плоскость в форме.
            // Масштаб тела: цель — выделенное тело (или выберут после).
            if (itemId === 'scale') {
                const sel = useCadStore.getState().selection;
                useCadStore.getState().setPendingFeature({
                    kind: 'scale', factor: 2,
                    bodyId: sel.kind === 'body' ? sel.ids[0] : null,
                });
                return;
            }
            // Спираль: пружина и резьба (уроки 5–6 методички).
            if (itemId === 'coil') {
                useCadStore.getState().setPendingFeature({
                    kind: 'coil', radius: 20, pitch: 8, height: 40,
                    wireDiameter: 3, section: 'circle', lefthand: false,
                });
                return;
            }
            if (itemId === 'hole')      { handleNewHole(); return; }
            if (itemId === 'mirror')    { handleNewMirror(); return; }
            if (itemId === 'pattern')   { handleNewPattern(); return; }
            if (itemId === 'circPattern') { handleNewCircPattern(); return; }
            if (['box', 'cylinder', 'sphere', 'torus'].includes(itemId)) {
                handleNewPrimitive(itemId); return;
            }
        }
        // === MODIFY ===
        if (groupId === 'modify') {
            if (itemId === 'combine')                       { handleNewCombine(); return; }
            if (itemId === 'fillet' || itemId === 'chamfer'){ handleNewFilletChamfer(itemId); return; }
            if (itemId === 'moveCopy')                       { handleNewMoveCopy(); return; }
            if (itemId === 'shell')                          { handleNewShell(); return; }
            if (itemId === 'changeParameters')               { useCadStore.getState().setParametersModalOpen(true); return; }
        }
        // === СБОРКА ===
        if (groupId === 'assemble') {
            if (itemId === 'joint') { handleNewJoint(); return; }
        }
        if (groupId === 'create') {
            if (itemId === 'rib') { handleNewRib('rib'); return; }
            if (itemId === 'web') { handleNewRib('web'); return; }
        }
        // === CONSTRUCT (рабочая геометрия) ===
        if (groupId === 'construct') {
            if (itemId === 'offsetPlane') { handleNewOffsetPlane(); return; }
            if (itemId === 'offsetPlane2') { handleNewOffsetPlane(); return; }
            if (itemId === 'planeAtAngle') { handleNewPlaneAtAngle(); return; }
            if (itemId === 'midplane') { handleNewMidplane(); return; }
            if (itemId === 'planeThroughThree') { handleNewPlaneThroughThree(); return; }
            if (itemId === 'axisThroughTwoPoints') { handleNewWorkaxis(); return; }
        }
        // === INSPECT (анализ) ===
        if (groupId === 'inspect' && itemId === 'measure') {
            handleNewMeasure(); return;
        }
        if (groupId === 'inspect' && itemId === 'massProps') {
            useCadStore.getState().setMeasureModalOpen(true); return;
        }
        // === INSERT ===
        if (groupId === 'insert' && itemId === 'importSTEP') { handleImportSTEP(); return; }

        // === SKETCH MODE — кнопки ribbon'а в режиме эскиза ===
        // СОЗДАТЬ-инструменты переключают activeSketchTool. Те, что
        // реализованы в SketchOverlay: line/rectangle/circle/arc.
        // Кнопки из выпадающего «more» имеют суффикс «2» (line2/arc2…) —
        // отбрасываем его, чтобы инструмент совпал.
        if (groupId === 'sketchCreate') {
            const toolId = itemId.replace(/2$/, '');
            if (['line', 'rectangle', 'circle', 'arc', 'polygon', 'ellipse', 'slot', 'point', 'constructionLine', 'spline'].includes(toolId)) {
                useCadStore.getState().setActiveSketchTool(toolId);
                return;
            }
            /* Текст: включаем инструмент и заводим пустой черновик. Надпись и
               размер вводятся в панели над сценой, а КЛИК по плоскости ставит
               буквы в это место — как в 123D. */
            if (toolId === 'text') {
                const st = useCadStore.getState();
                if (!st.textDraft) st.setTextDraft({ text: '', fontSize: 10 });
                st.setActiveSketchTool('sketchText');
                return;
            }
            // Инструмент «Размер» — id кнопки sketchDimension → tool 'dimension'.
            if (toolId === 'sketchDimension') {
                useCadStore.getState().setActiveSketchTool('dimension');
                return;
            }
            /*
             * Проецирование рёбер тела в эскиз.
             *
             * 🔴 Обработчик должен лежать в ТОЙ ЖЕ группе, что и пункт меню:
             * сам пункт в sketchCreate, а не в sketchModify. Иначе ветка не
             * срабатывает — кнопка нажимается, и ничего не происходит.
             */
            if (toolId === 'project') {
                useCadStore.getState().setActiveSketchTool(null);
                useCadStore.getState().setRequestConstraint('project');
                return;
            }
        }
        // ИЗМЕНИТЬ (эскиз) — trim: инструмент «Обрезать», клик по примитиву
        // удаляет его (простой предсказуемый trim для детей).
        if (groupId === 'sketchModify') {
            const toolId = itemId.replace(/2$/, '');
            if (toolId === 'trim') {
                useCadStore.getState().setActiveSketchTool('trim');
                return;
            }
            if (toolId === 'sketchOffset') {
                useCadStore.getState().setActiveSketchTool('offsetSketch');
                return;
            }
            // Скругление угла: клик по вершине двух линий заменяет угол дугой.
            if (toolId === 'sketchFillet') {
                useCadStore.getState().setActiveSketchTool('sketchFillet');
                return;
            }
            // Продлить: клик по линии дотягивает её до соседней.
            if (toolId === 'extend') {
                useCadStore.getState().setActiveSketchTool('extendSketch');
                return;
            }
            /*
             * Зеркало и массив работают по ВЫДЕЛЕННЫМ примитивам, а не по
             * клику: копируют обычно несколько объектов сразу. Поэтому идут
             * тем же каналом, что и ограничения, — через requestConstraint.
             */
            if (toolId === 'sketchMirror' || toolId === 'sketchPattern') {
                useCadStore.getState().setActiveSketchTool(null);
                useCadStore.getState().setRequestConstraint(toolId);
                return;
            }
        }
        // ВЫБРАТЬ — сбрасывает активный инструмент рисования. Без
        // активного инструмента клик по линии/окружности ВЫДЕЛЯЕТ её
        // (для применения ограничений), а не рисует новую фигуру.
        if (groupId === 'sketchSelect' && itemId === 'selectMode') {
            useCadStore.getState().setActiveSketchTool(null);
            return;
        }
        // Геометрические ограничения — применяются к выделенным
        // примитивам эскиза. Кнопки 'more' имеют суффикс '2'.
        if (groupId === 'constraints') {
            const kind = itemId.replace(/2$/, '');
            // Как в Fusion: при выборе ограничения курсор переходит в
            // режим выбора объектов — сбрасываем инструмент рисования,
            // иначе клики по примитивам будут рисовать, а не выделять.
            useCadStore.getState().setActiveSketchTool(null);
            useCadStore.getState().setRequestConstraint(kind);
            return;
        }
        // Завершить эскиз — выйти из sketch-режима в view3d.
        if (groupId === 'finishSketch' && itemId === 'finishSketch') {
            useCadStore.getState().exitSketchMode();
            return;
        }

        // Заглушка
        console.log('[TopBar] not implemented:', groupId, itemId);
    };

    const onItemClick = (groupId, item) => {
        if (item.disabled || item.sep) return;
        setOpenMenu(null);
        dispatchAction(groupId, item.id);
    };

    return (
        <div className={cl.topbar} ref={containerRef}>
            {searchOpen && (
                <CommandSearch
                    commands={allCommands}
                    onRun={(cmd) => dispatchAction(cmd.groupId, cmd.id)}
                    onClose={() => setSearchOpen(false)}
                />
            )}
            {/* === Workspace tabs (SOLID / SURFACE / ...) === */}
            <div className={cl.workspaceRow}>
                {/* Кнопка File слева */}
                <div className={cl.fileBox}>
                    <button
                        className={cl.fileBtn}
                        onClick={() => setOpenMenu(openMenu === 'file' ? null : 'file')}
                        title="Файл"
                    >
                        <IconFile size={18}/>
                        <IconChevronDown size={9}/>
                    </button>
                    {openMenu === 'file' && (
                        <div className={cl.fileDropdown}>
                            {FILE_MENU.map((it) => it.sep ? (
                                <div key={it.id} className={cl.menuSep}/>
                            ) : (
                                <button
                                    key={it.id}
                                    className={cl.menuItem}
                                    onClick={() => onItemClick('file', it)}
                                >
                                    {it.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Workspace tabs */}
                {WORKSPACES.map((w) => (
                    <button
                        key={w.id}
                        className={`${cl.wsTab} ${activeWorkspace === w.id ? cl.wsTabActive : ''} ${!w.active ? cl.wsTabDisabled : ''}`}
                        onClick={() => w.active && setActiveWorkspace(w.id)}
                        disabled={!w.active}
                    >
                        {w.label}
                    </button>
                ))}
            </div>

            {/* === Ribbon с группами === */}
            {/* Контент меняется по mode: обычный RIBBON / SKETCH_RIBBON. */}
            <div className={cl.ribbonRow}>
                {ribbon.map((g, idx) => (
                    <React.Fragment key={g.id}>
                        <RibbonGroup
                            group={g}
                            isMoreOpen={openMenu === `more:${g.id}`}
                            onToggleMore={() =>
                                setOpenMenu(openMenu === `more:${g.id}` ? null : `more:${g.id}`)
                            }
                            onItemClick={(item) => onItemClick(g.id, item)}
                            activeId={
                                // Нет активного инструмента рисования →
                                // подсвечена кнопка «Выбрать» (режим выбора).
                                isSketchMode && activeSketchTool == null
                                    ? 'selectMode' : activeSketchTool
                            }
                        />
                        {idx < ribbon.length - 1 && <div className={cl.groupDivider}/>}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};


// === Создать новый эскиз — переход в режим выбора плоскости ===
function handleNewSketch() {
    const store = useCadStore.getState();
    store.enterSelectPlaneMode();
}


// === Запустить операцию «Выдавить» ===
// Поддерживает два источника:
//   - selection.kind === 'profile'  → классический extrude профиля эскиза
//   - selection.kind === 'face'     → extrude грани существующего тела
//                                     (Press Pull в Fusion 360)
// Если ничего не выбрано — pendingFeature без profileRefs/face, юзер сам
// кликает в сцене по контуру или грани, и Properties.ExtrudeForm подставит.
function handleNewExtrude() {
    const store = useCadStore.getState();
    const sel = store.selection;

    // Вариант 1: выбран профиль эскиза
    if (sel.kind === 'profile' && sel.ids.length > 0) {
        store.setPendingFeature({
            kind: 'extrude',
            profileRefs: sel.ids,
            distance: 20,
            direction: 'one',
            operation: 'new',
        });
        return;
    }

    // Вариант 2: выбрана грань тела — превращаем в полигональный профиль
    // в локальной 2D-системе плоскости (XY/XZ/YZ, как в replicad).
    if (sel.kind === 'face') {
        const faceData = store.selectedFaceData;
        if (faceData && faceData.points3d && faceData.planeRef) {
            const profile = buildFaceProfile(faceData);
            if (profile) {
                store.setPendingFeature({
                    kind: 'extrude',
                    // Используем поле faceProfile, чтобы Properties и Preview
                    // могли отличить face-extrude от sketch-extrude.
                    faceProfile: profile,
                    faceRef: sel.ids[0],
                    distance: 20,
                    direction: 'one',
                    // По умолчанию ADD (выдавливаем грань → прибавляем материал к телу).
                    // Юзер может переключить в Properties: new / cut / intersect.
                    operation: 'add',
                    targetBodyId: faceData.bodyId,
                });
                return;
            }
        }
    }

    // Иначе — пустая операция, ждём пока юзер выберет
    store.setPendingFeature({
        kind: 'extrude',
        profileRefs: [],
        distance: 20,
        direction: 'one',
        operation: 'new',
    });
}


// === Вращение (Revolve) — выдавить профиль вокруг оси ===
function handleNewRevolve() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const profileRefs = (sel.kind === 'profile' && sel.ids.length > 0) ? sel.ids : [];
    store.setPendingFeature({
        kind: 'revolve',
        profileRefs,
        axis: 'sketch_Y',   // ось вращения по умолчанию — вертикаль эскиза
        angle: 360,
        operation: 'new',
    });
}




// === Создать примитив (Box / Cylinder / Sphere / Torus) ===
// Открывает форму в Properties с параметрами по умолчанию.
// PrimitiveForm в Properties сразу создаёт preview-тело в сцене (live-update).
// При изменении параметров / position тело пересоздаётся (debounced 120мс).
// Кнопка OK — фиксирует тело как feature в Timeline.
function handleNewPrimitive(kind) {
    const store = useCadStore.getState();
    const defaults = {
        box:      { width: 50, depth: 50, height: 50 },
        cylinder: { radius: 25, height: 50 },
        sphere:   { radius: 30 },
        torus:    { radius: 30, tube: 8 },
    };
    store.setPendingFeature({
        kind: 'primitive',
        primitiveKind: kind,           // 'box' | 'cylinder' | 'sphere' | 'torus'
        ...defaults[kind],
        position: [0, 0, 0],
        operation: 'new',              // primitive по умолчанию — новое тело
    });
}


// === Оболочка (Shell) — превращает тело в стенки толщины t,
// удаляя выбранные грани. Если уже выделена грань — добавляем сразу. ===
function handleNewShell() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const facesInfo = []; // [{ bodyId, faceId, center: [x,y,z] }, ...]
    if (sel.kind === 'face' && store.selectedFaceData) {
        const fd = store.selectedFaceData;
        if (fd.planeRef?.center) {
            facesInfo.push({
                bodyId: fd.bodyId,
                faceId: fd.faceId,
                center: fd.planeRef.center,
            });
        }
    }
    store.setPendingFeature({
        kind: 'shell',
        thickness: 2,
        faces: facesInfo,
    });
}


// === Отверстие ===
// Если уже выделена грань — берём её как стартовую. Иначе форма пустая,
// юзер кликает по грани тела. Отверстие сверлится в центре грани (можно
// сместить через поля X/Y в форме).
function handleNewHole() {
    const store = useCadStore.getState();
    const sel = store.selection;
    let faceInfo = null;
    if (sel.kind === 'face' && store.selectedFaceData) {
        const fd = store.selectedFaceData;
        if (fd.planeRef) {
            faceInfo = {
                bodyId: fd.bodyId,
                faceId: fd.faceId,
                planeRef: fd.planeRef,
                faceCenter: fd.planeRef.center || null,
            };
        }
    }
    store.setPendingFeature({
        kind: 'hole',
        faceInfo,
        diameter: 8,
        depth: 20,
        depthType: 'through',  // 'through' | 'blind'
        offsetX: 0,
        offsetY: 0,
    });
}


// === Зеркало ===
// Если выделено тело — берём его. Иначе форма с пустым слотом,
// юзер докликивает тело в дереве/сцене.
function handleNewMirror() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const bodyId = (sel.kind === 'body' && sel.ids.length > 0)
        ? sel.ids[0] : null;
    store.setPendingFeature({
        kind: 'mirror',
        bodyId,
        plane: 'YZ',  // плоскость симметрии: 'XY' | 'XZ' | 'YZ'
    });
}


// === Прямоугольный массив ===
// Сетка N×M копий тела с заданным шагом по X и Y.
function handleNewPattern() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const bodyId = (sel.kind === 'body' && sel.ids.length > 0)
        ? sel.ids[0] : null;
    store.setPendingFeature({
        kind: 'rectPattern',
        bodyId,
        countX: 3, stepX: 30,
        countY: 1, stepY: 30,
    });
}


// === Круговой массив ===
// N копий тела по окружности вокруг оси (X/Y/Z).
function handleNewCircPattern() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const bodyId = (sel.kind === 'body' && sel.ids.length > 0)
        ? sel.ids[0] : null;
    store.setPendingFeature({
        kind: 'circPattern',
        bodyId,
        axis: 'Z',     // ось вращения: 'X' | 'Y' | 'Z'
        count: 6,      // число копий всего
        angle: 360,    // полный угол охвата (градусы)
    });
}


// === Рабочая плоскость со смещением ===
// Плоскость, параллельная базовой (XY/XZ/YZ), сдвинутая на N мм.
// На ней можно создавать эскизы.
function handleNewOffsetPlane() {
    useCadStore.getState().setPendingFeature({
        kind: 'offsetPlane',
        basePlane: 'XY',
        offset: 30,
    });
}


// Плоскость под углом: базовая плоскость + поворот на угол вокруг оси.
function handleNewPlaneAtAngle() {
    useCadStore.getState().setPendingFeature({
        kind: 'planeAtAngle',
        basePlane: 'XY',
        rotAxis: 'X',   // ось поворота (лежит в базовой плоскости)
        angle: 45,
    });
}


// Срединная плоскость: посередине между двумя параллельными базовыми
// плоскостями со смещениями (или между двумя гранями — упрощённо через offset).
function handleNewMidplane() {
    useCadStore.getState().setPendingFeature({
        kind: 'midplane',
        basePlane: 'XY',
        offset1: 0,
        offset2: 40,
    });
}


// Плоскость через 3 точки (координаты вводятся числами).
function handleNewPlaneThroughThree() {
    useCadStore.getState().setPendingFeature({
        kind: 'planeThroughThree',
        p1: [0, 0, 0], p2: [50, 0, 0], p3: [0, 50, 20],
    });
}


// === Рабочая ось (через 2 точки) ===
// Произвольная ось в пространстве. Используется как ось вращения
// для кругового массива.
function handleNewWorkaxis() {
    useCadStore.getState().setPendingFeature({
        kind: 'workaxis',
        p1: [0, 0, 0],
        p2: [0, 0, 50],
    });
}


// === Измерение (как во Fusion 360) ===
// Инструмент анализа — НЕ создаёт геометрию. Копит до ДВУХ пиков
// (грань / ребро / вершина) в pendingFeature.picks; при наличии 2 (или 1)
// пиков worker считает расстояние/угол/свойства (см. MeasurePanel).
function handleNewMeasure() {
    useCadStore.getState().setPendingFeature({
        kind: 'measure',
        picks: [],     // [{ bodyId, kind:'face'|'edge'|'vertex', point, normal?, mid?, len?, render:{...} }]
        result: null,  // { count, distance?, angle?, delta?, entities, p1?, p2? }
    });
}


// === Переместить / скопировать тело ===
// Если выделено body — открываем форму с {bodyId, dx=0, dy=0, dz=0, ...}.
// Если ничего не выделено — форма с bodyId=null, юзер докликнет в дереве.
//
// initialCenter — bbox-центр body на момент старта операции. Зафиксирован
// чтобы TransformGizmo не дёргался: при live-preview body двигается, его
// bbox-центр меняется, но gizmo должен опираться на ИСХОДНЫЙ центр.
/*
 * Механическое соединение между двумя ТЕЛАМИ.
 *
 * Компонентов у нас нет и пока не планируется: для робототехники достаточно
 * связать две детали и проверить ход механизма.
 *
 * Первое выделенное тело — основание (неподвижное), второе — подвижное.
 * Если выделено одно, второе выбирается в панели.
 */
/*
 * Ребро жёсткости (rib) или стенка (web).
 *
 * Обе работают с НЕЗАМКНУТОЙ линией эскиза — ребро рисуют одной чертой
 * поперёк корпуса. Ищем такие цепочки во всех эскизах документа; если
 * подходящей нет, честно говорим об этом, а не открываем пустую панель.
 */
function handleNewRib(kind) {
    const store = useCadStore.getState();
    const sketches = store.doc.sketches || [];
    for (const sk of sketches) {
        const chains = findOpenChains(sk);
        if (chains.length === 0) continue;
        store.setPendingFeature({
            kind: 'rib',
            ribKind: kind,
            sketchId: sk.id,
            chainId: chains[0].id,
            thickness: 2,
            depth: 10,
            targetBodyId: null,
        });
        return;
    }
    toast(
        'Нужна незамкнутая линия в эскизе: ребро рисуют одной чертой, ' +
        'а не замкнутым контуром. Начерти линию и повтори.',
        'info',
    );
}


function handleNewJoint() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const ids = (sel.kind === 'body') ? sel.ids : [];
    const bodyA = ids[0] || null;
    const bodyB = ids[1] || null;
    // Точка соединения по умолчанию — центр подвижного тела: чаще всего
    // шарнир там и находится, а поправить проще, чем задавать с нуля.
    let origin = [0, 0, 0];
    const ref = bodyB || bodyA;
    if (ref) {
        const mesh = store.bodyMeshes.get(ref);
        if (mesh) origin = computeBodyCenter(mesh);
    }
    store.setPendingFeature({
        kind: 'joint',
        jointKind: 'revolute',
        bodyA, bodyB,
        origin,
        axis: [0, 0, 1],
        value: 0,
        min: null, max: null,
    });
}


function handleNewMoveCopy() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const bodyId = (sel.kind === 'body' && sel.ids.length > 0) ? sel.ids[0] : null;
    let initialCenter = [0, 0, 0];
    if (bodyId) {
        const mesh = store.bodyMeshes.get(bodyId);
        if (mesh) initialCenter = computeBodyCenter(mesh);
    }
    store.setPendingFeature({
        kind: 'moveCopy',
        bodyId,
        initialCenter,
        dx: 0, dy: 0, dz: 0,
        rx: 0, ry: 0, rz: 0,
        copy: false,
    });
}


// Bbox-центр тела по mesh.positions (нужен для зафиксированной позиции gizmo).
function computeBodyCenter(mesh) {
    const p = mesh?.positions;
    if (!p || p.length < 3) return [0, 0, 0];
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < p.length; i += 3) {
        if (p[i] < minX) minX = p[i]; if (p[i] > maxX) maxX = p[i];
        if (p[i+1] < minY) minY = p[i+1]; if (p[i+1] > maxY) maxY = p[i+1];
        if (p[i+2] < minZ) minZ = p[i+2]; if (p[i+2] > maxZ) maxZ = p[i+2];
    }
    return [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
}


// === Логические операции между готовыми телами (Combine) ===
// Если выбраны 2+ тела — берём первое как target, второе как tool.
// Если выбрано меньше — открываем форму с пустыми слотами, юзер докликивает.
function handleNewCombine() {
    const store = useCadStore.getState();
    const sel = store.selection;
    const bodyIds = (sel.kind === 'body') ? sel.ids : [];
    store.setPendingFeature({
        kind: 'combine',
        targetId: bodyIds[0] || null,
        toolId:   bodyIds[1] || null,
        operation: 'add',  // 'add' | 'cut' | 'intersect'
        keepTool: false,
    });
}


// === Скругление / Фаска ===
// Если уже есть выделенные рёбра — используем их.
// Иначе форма с пустым списком — юзер кликает по рёбрам, они добавляются.
function handleNewFilletChamfer(kind /* 'fillet' | 'chamfer' */) {
    const store = useCadStore.getState();
    const sel = store.selection;
    const edgeRefs = (sel.kind === 'edge') ? sel.ids : [];
    store.setPendingFeature({
        kind,                    // 'fillet' | 'chamfer'
        edgeRefs,                // массив 'bodyId/edgeId'
        radius: kind === 'fillet' ? 3 : 2,
    });
}


// =====================================================================
// === Файл: Новый / Открыть / Сохранить как =========================
// =====================================================================

// Новый документ — очищает store и worker. Спрашивает подтверждение
// если в текущем документе есть несохранённые изменения.
async function handleNewDocument() {
    const store = useCadStore.getState();
    if (store.isModified) {
        if (!window.confirm('Текущий документ изменён. Создать новый и потерять изменения?')) {
            return;
        }
    }
    // Чистим worker (удаляет тела) и store (новый пустой документ)
    try {
        await getGeometryClient().clearAll();
    } catch (_) { /* worker мог быть не инициализирован — норм */ }
    store.newDocument();
    // Также чистим автосохранение, чтобы не «всплыло» при следующем старте
    try { await autosave.clear(); } catch (_) {}
}

// Открыть .kubikon.json — file picker → JSON.parse → loadDocumentFromJson.
async function handleOpenDocument() {
    const store = useCadStore.getState();
    if (store.isModified) {
        if (!window.confirm('Текущий документ изменён. Открыть другой и потерять изменения?')) {
            return;
        }
    }
    try {
        const res = await openDocumentFile();
        if (res.cancelled) return;
        await loadDocumentFromJson(res.data);
    } catch (e) {
        console.error('[TopBar] open failed:', e);
        toast('Ошибка открытия: ' + e.message, 'error');
    }
}

// Сохранить как — скачивает .kubikon.json. Имя файла предлагает по title.
function handleSaveAsDocument() {
    const store = useCadStore.getState();
    const title = store.doc?.title || 'document';
    try {
        downloadDocumentFile(title);
        store.markSaved();
    } catch (e) {
        console.error('[TopBar] saveAs failed:', e);
        toast('Ошибка сохранения: ' + e.message, 'error');
    }
}

// Сохранить в облако — открываем модалку с полями title/description/isPublic.
// Модалка сама сделает POST/PUT через KubikonCadService.
function handleCloudSave() {
    useCadStore.getState().setCloudSaveModalOpen(true);
}


// === Экспорт STL (для 3D-печати) ===
function handleExportSTL(bodyMeshes) {
    return handleExportFormat(bodyMeshes, 'stl');
}


// === Универсальный экспорт: stl | step | obj ===
// Для MVP — экспортируем ПЕРВОЕ тело документа. Когда тел несколько,
// логика будет: выделено одно → его, иначе компонуем в один файл.
async function handleExportFormat(bodyMeshes, fmt) {
    if (!bodyMeshes || bodyMeshes.size === 0) {
        toast('Нет тел для экспорта. Создай хотя бы одно (Создать → Параллелепипед).', 'error');
        return;
    }
    const client = getGeometryClient();
    // Если у юзера выделено тело — берём его, иначе первое в документе.
    const sel = useCadStore.getState().selection;
    const hasSelected = sel.kind === 'body' && sel.ids.length === 1;
    const firstId = hasSelected ? sel.ids[0] : bodyMeshes.keys().next().value;
    // Экспортируем одно тело. Если их несколько и не выделено конкретное —
    // честно предупреждаем, что в файл уйдёт только первое (не молча).
    if (!hasSelected && bodyMeshes.size > 1) {
        toast(`В сцене ${bodyMeshes.size} тел — в файл уйдёт только первое. ` +
              `Выдели нужное тело перед экспортом.`, 'info');
    }

    try {
        let blob;
        let ext;
        if (fmt === 'stl') {
            blob = await client.exportSTL(firstId);
            ext = 'stl';
        } else if (fmt === 'step') {
            blob = await client.exportSTEP(firstId);
            ext = 'step';
        } else if (fmt === 'obj') {
            blob = await client.exportOBJ(firstId);
            ext = 'obj';
        } else {
            throw new Error('Неизвестный формат: ' + fmt);
        }

        const docTitle = useCadStore.getState().doc?.title || firstId;
        const fname = sanitizeName(docTitle) + '.' + ext;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
        console.error('[TopBar] export failed:', e);
        toast(`Ошибка экспорта ${fmt.toUpperCase()}: ` + (e.message || e), 'error');
    }
}


function sanitizeName(name) {
    return String(name)
        .replace(/[/\\?%*:|"<>]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 80) || 'document';
}


// === Импорт STEP (Месяц 10 плана) ===
// Открывает file picker, читает файл как ArrayBuffer, шлёт в worker.
// Worker создаёт shape через replicad.importSTEP и отдаёт mesh обратно
// — тело появляется в сцене как обычное body_<N>.
async function handleImportSTEP() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.step,.stp,.iges,.igs';
    input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const client = getGeometryClient();
        if (!client.ready) {
            toast('Ядро ещё не загружено, подожди несколько секунд', 'error');
            return;
        }
        try {
            const buf = await file.arrayBuffer();
            await client.importSTEP(buf, file.name);
        } catch (e) {
            console.error('[TopBar] importSTEP failed:', e);
            toast('Ошибка импорта STEP: ' + (e.message || e), 'error');
        }
    };
    input.click();
}

export default TopBar;
